import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AddInboundMessageInput,
  Conversation,
  ConversationOutcome,
  ConversationStatus,
  CreateConversationInput,
  Customer,
  Draft,
  LogSentMessageInput,
  Message,
  Topic,
  UpdateConversationInput,
} from '@anchor-comms/shared';
import { api } from '@/lib/api';

export interface ConversationListItem {
  id: string;
  status: ConversationStatus;
  outcome: ConversationOutcome | null;
  createdAt: string;
  updatedAt: string;
  closedAt: string | null;
  customer: { id: string; name: string; company: string | null };
  topic: { id: string; name: string; slug: string };
  rep: { id: string; name: string };
  _count: { messages: number; drafts: number };
}

export interface ConversationDetail extends Conversation {
  customer: Customer;
  topic: Topic;
  rep: { id: string; name: string; email: string; role: 'rep' | 'manager' };
  messages: Message[];
  drafts: Draft[];
}

export function useConversationsList(params: {
  status?: ConversationStatus;
  outcome?: ConversationOutcome;
  topicId?: string;
  repId?: string;
}) {
  return useQuery({
    queryKey: ['conversations', params],
    queryFn: () =>
      api<{ items: ConversationListItem[]; page: { nextCursor: string | null } }>(
        '/api/v1/conversations',
        { query: { ...params, limit: 50 } },
      ),
  });
}

export function useConversation(id: string | undefined) {
  return useQuery({
    queryKey: ['conversation', id],
    queryFn: () => api<ConversationDetail>(`/api/v1/conversations/${id}`),
    enabled: !!id,
  });
}

export function useCreateConversation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateConversationInput) =>
      api<ConversationDetail>('/api/v1/conversations', { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['conversations'] }),
  });
}

export function useUpdateConversation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateConversationInput) =>
      api<ConversationDetail>(`/api/v1/conversations/${id}`, { method: 'PATCH', body: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['conversation', id] });
      qc.invalidateQueries({ queryKey: ['conversations'] });
    },
  });
}

export function useAddInboundMessage(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddInboundMessageInput) =>
      api<Message>(`/api/v1/conversations/${id}/messages`, { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['conversation', id] }),
  });
}

export function useLogSentMessage(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LogSentMessageInput) =>
      api<Message>(`/api/v1/conversations/${id}/messages/sent`, { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['conversation', id] }),
  });
}
